import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { usePolicy } from './PolicyContext';
import { getIdToken } from '../utils/keycloak';
import {
  SceneAsset,
  AssetType,
  listAssets as listAssetsApi,
  uploadAsset as uploadAssetApi,
  deleteAsset as deleteAssetApi,
} from '../services/sceneAssetService';

/** Cache TTL: 45 minutes (signed URLs valid for 60 minutes) */
const CACHE_TTL_MS = 45 * 60 * 1000;

interface CachedAssetList {
  assets: SceneAsset[];
  fetchedAt: number;
}

/** Module-level cache — persists across React unmounts (same pattern as useSceneConfig) */
const assetCache = new Map<string, CachedAssetList>();

/** Roles that can open /roleplay — mirrors the Role Play entry in layout/SideBar.tsx. */
const ROLEPLAY_ROLES: ReadonlySet<string> = new Set(['super_admin', 'admin', 'trainer', 'trainee']);

/** Whose assets `assetCache` currently holds — see `identityKey` in SceneAssetProvider. */
let assetCacheIdentity: string | null = null;

function cacheKey(type?: AssetType, orgFilter?: string): string {
  return `${type ?? 'all'}:${orgFilter ?? ''}`;
}

function isCacheValid(entry: CachedAssetList | undefined): entry is CachedAssetList {
  return !!entry && Date.now() - entry.fetchedAt < CACHE_TTL_MS;
}

interface SceneAssetContextType {
  /** Get cached assets by type. Pass undefined for all assets. */
  assets: (type?: AssetType) => SceneAsset[];
  /** Whether a specific asset type (or 'all') is currently loading */
  isLoading: (type?: AssetType) => boolean;
  /** Error message for a specific asset type (or 'all') */
  error: (type?: AssetType) => string | null;
  /** Ensure assets of a given type are loaded (triggers fetch if cache is stale) */
  ensureLoaded: (type?: AssetType) => void;
  /** Upload a new asset. Automatically invalidates relevant caches. */
  uploadAsset: (
    file: File,
    assetType: AssetType,
    name: string,
    metadata?: Record<string, any>,
    onProgress?: (percent: number) => void,
    organizationId?: string,
  ) => Promise<SceneAsset>;
  /** Delete an asset. Automatically invalidates relevant caches. */
  deleteAsset: (assetId: string) => Promise<void>;
  /** Clear cache and re-fetch for a specific type (or all) */
  invalidate: (type?: AssetType) => void;
  /**
   * Set the org filter for asset fetching (SA scene editor use).
   * Pass undefined to reset to the default behavior (platform-only for SA with no org,
   * or user's own org for regular users).
   */
  setOrgFilter: (orgId: string | undefined) => void;
}

const SceneAssetContext = createContext<SceneAssetContextType | undefined>(undefined);

export const SceneAssetProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const {
    token,
    currentUser,
    userRole,
    noOrganization,
    isTrialExhausted,
    subscriptionCancelled,
  } = useAuth();
  const { policiesAccepted } = usePolicy();
  const hasToken = !!token;

  // Who the cached lists belong to. The token rotates every few minutes for the same
  // person, so it must not be the cache key — that cleared and refetched the full asset
  // list on every refresh. The effective org and role (which a demo proxy changes
  // without changing the user) are what decide which assets the backend returns.
  // Empty until the user record is known, so a token that lands a render before the
  // user doesn't cost an extra fetch-then-clear.
  const identityKey = hasToken && currentUser
    ? `${currentUser.id}:${currentUser.organization_id ?? ''}:${userRole ?? ''}`
    : '';

  // org filter: used by SA in scene editor to scope asset list to a specific org
  const [orgFilter, setOrgFilterState] = useState<string | undefined>(undefined);

  // Track loading/error per cache key
  const [loadingKeys, setLoadingKeys] = useState<Set<string>>(new Set());
  const [errorMap, setErrorMap] = useState<Map<string, string>>(new Map());

  // Bump to trigger re-renders when cache updates
  const [version, setVersion] = useState(0);

  // Track in-flight requests to avoid duplicates
  const inFlightRef = useRef<Set<string>>(new Set());

  const bumpVersion = useCallback(() => setVersion(v => v + 1), []);

  // Bumped whenever the identity changes, so a request issued for the previous user/org
  // can't land in the cache after it was cleared for the new one.
  const generationRef = useRef(0);

  const fetchAssets = useCallback(async (type?: AssetType, currentOrgFilter?: string) => {
    const key = cacheKey(type, currentOrgFilter);
    const inFlight = inFlightRef.current;
    if (!token || inFlight.has(key)) return;

    // Skip if cache is still fresh
    if (isCacheValid(assetCache.get(key))) return;

    const generation = generationRef.current;
    inFlight.add(key);
    setLoadingKeys(prev => new Set(prev).add(key));
    setErrorMap(prev => { const m = new Map(prev); m.delete(key); return m; });

    try {
      const freshToken = await getIdToken();
      const assets = await listAssetsApi(freshToken, type, currentOrgFilter);
      if (generation !== generationRef.current) return; // identity changed mid-request
      assetCache.set(key, { assets, fetchedAt: Date.now() });

      // When fetching 'all', also populate per-type caches for this org context
      if (!type) {
        const byType = new Map<AssetType, SceneAsset[]>();
        for (const asset of assets) {
          const list = byType.get(asset.asset_type) || [];
          list.push(asset);
          byType.set(asset.asset_type, list);
        }
        const now = Date.now();
        for (const [assetType, typeAssets] of byType) {
          assetCache.set(cacheKey(assetType, currentOrgFilter), { assets: typeAssets, fetchedAt: now });
        }
      }

      bumpVersion();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load assets';
      setErrorMap(prev => new Map(prev).set(key, msg));
    } finally {
      inFlight.delete(key);
      setLoadingKeys(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  }, [token, bumpVersion]);

  const getAssets = useCallback((type?: AssetType): SceneAsset[] => {
    const key = cacheKey(type, orgFilter);
    const cached = assetCache.get(key);
    return cached?.assets ?? [];
  }, [version, orgFilter]);

  const getIsLoading = useCallback((type?: AssetType): boolean => {
    return loadingKeys.has(cacheKey(type, orgFilter));
  }, [loadingKeys, orgFilter]);

  const getError = useCallback((type?: AssetType): string | null => {
    return errorMap.get(cacheKey(type, orgFilter)) ?? null;
  }, [errorMap, orgFilter]);

  const ensureLoaded = useCallback((type?: AssetType) => {
    const key = cacheKey(type, orgFilter);
    if (!isCacheValid(assetCache.get(key)) && !inFlightRef.current.has(key)) {
      fetchAssets(type, orgFilter);
    }
  }, [fetchAssets, orgFilter]);

  const invalidate = useCallback((type?: AssetType) => {
    if (type) {
      assetCache.delete(cacheKey(type, orgFilter));
      assetCache.delete(cacheKey(undefined, orgFilter));
    } else {
      // Clear all entries for current org context
      for (const key of assetCache.keys()) {
        if (key.endsWith(`:${orgFilter ?? ''}`)) assetCache.delete(key);
      }
    }
    fetchAssets(type, orgFilter);
  }, [fetchAssets, orgFilter]);

  const setOrgFilter = useCallback((orgId: string | undefined) => {
    setOrgFilterState(prev => {
      if (prev === orgId) return prev;
      // Evict only the old org context's cache entries — other contexts stay warm.
      for (const key of assetCache.keys()) {
        if (key.endsWith(`:${prev ?? ''}`)) assetCache.delete(key);
      }
      return orgId;
    });
  }, []);

  const handleUpload = useCallback(async (
    file: File,
    assetType: AssetType,
    name: string,
    metadata?: Record<string, any>,
    onProgress?: (percent: number) => void,
    organizationId?: string,
  ): Promise<SceneAsset> => {
    if (!token) throw new Error('Not authenticated');
    const freshToken = await getIdToken();
    const newAsset = await uploadAssetApi(freshToken, file, assetType, name, metadata, onProgress, organizationId);

    // Optimistically add to the correct org context cache.
    // If the upload was on behalf of a specific org (SA override), use that org as the
    // cache key so the asset appears immediately in the right context, not the viewer's.
    const effectiveOrg = organizationId ?? orgFilter;
    const typeKey = cacheKey(assetType, effectiveOrg);
    const allKey = cacheKey(undefined, effectiveOrg);
    const now = Date.now();
    const typeCached = assetCache.get(typeKey);
    if (typeCached) {
      assetCache.set(typeKey, { assets: [newAsset, ...typeCached.assets], fetchedAt: now });
    }
    const allCached = assetCache.get(allKey);
    if (allCached) {
      assetCache.set(allKey, { assets: [newAsset, ...allCached.assets], fetchedAt: now });
    }

    bumpVersion();
    return newAsset;
  }, [token, bumpVersion, orgFilter]);

  const handleDelete = useCallback(async (assetId: string): Promise<void> => {
    if (!token) throw new Error('Not authenticated');
    const freshToken = await getIdToken();
    await deleteAssetApi(freshToken, assetId);

    // Remove from all caches
    for (const [key, entry] of assetCache) {
      const filtered = entry.assets.filter(a => a.id !== assetId);
      if (filtered.length !== entry.assets.length) {
        assetCache.set(key, { assets: filtered, fetchedAt: entry.fetchedAt });
      }
    }

    bumpVersion();
  }, [token, bumpVersion]);

  // Clear module-level cache when the identity changes (logout, another user, or a demo
  // proxy switching effective org/role) to prevent cross-user data leaks. Not on token
  // rotation: same person, same assets.
  if (assetCacheIdentity !== identityKey) {
    // Cleared during render, before the effects below read the cache, so the auto-load
    // sees it empty and refetches for the new identity in the same commit. Compared with
    // the module-level owner (not a ref), because the cache outlives a provider remount.
    assetCacheIdentity = identityKey;
    assetCache.clear();
    generationRef.current += 1;
    inFlightRef.current = new Set();
  }

  // Auto-load all assets on mount when token is available AND policies have been accepted.
  // Gating on policiesAccepted avoids firing /scene-assets while the policy modal is blocking the app.
  // Keyed on identityKey rather than the token string, so a rotation is a no-op.
  // `fetchAssets` is deliberately left out: it changes identity with every token, and it
  // reads a fresh token itself (getIdToken), so the latest closure is never needed here.
  useEffect(() => {
    if (identityKey && policiesAccepted === true && !isCacheValid(assetCache.get(cacheKey(undefined, orgFilter)))) {
      fetchAssets(undefined, orgFilter);
    }
  }, [identityKey, policiesAccepted, orgFilter]);

  // Warm the browser cache with the default classroom's models while idle, so the first
  // Role Play visit doesn't download ~15MB behind "Loading classroom scene...". Only for
  // users who can actually open a roleplay; cancelled on logout or identity change.
  const canRoleplay =
    !!identityKey &&
    policiesAccepted === true &&
    !!userRole &&
    ROLEPLAY_ROLES.has(userRole) &&
    !noOrganization &&
    !isTrialExhausted &&
    !subscriptionCancelled;
  const tokenRef = useRef(token);
  tokenRef.current = token;
  useEffect(() => {
    const scheduleToken = tokenRef.current;
    if (!canRoleplay || !scheduleToken) return;
    // Loaded on demand so this provider (in the main bundle) doesn't pull the scene
    // config modules in with it.
    let cancel: (() => void) | null = null;
    let disposed = false;
    import('../components/classroom-scene/hooks/prefetchDefaultScene')
      .then(({ scheduleDefaultScenePrefetch }) => {
        if (!disposed) cancel = scheduleDefaultScenePrefetch(scheduleToken);
      })
      .catch(() => { /* best-effort: the page loads the scene itself */ });
    return () => {
      disposed = true;
      cancel?.();
    };
  }, [canRoleplay, identityKey]);

  const value: SceneAssetContextType = {
    assets: getAssets,
    isLoading: getIsLoading,
    error: getError,
    ensureLoaded,
    uploadAsset: handleUpload,
    deleteAsset: handleDelete,
    invalidate,
    setOrgFilter,
  };

  return (
    <SceneAssetContext.Provider value={value}>
      {children}
    </SceneAssetContext.Provider>
  );
};

export function useSceneAssets(): SceneAssetContextType {
  const ctx = useContext(SceneAssetContext);
  if (!ctx) {
    throw new Error('useSceneAssets must be used within a SceneAssetProvider');
  }
  return ctx;
}
